import { useEffect, useState, type RefObject } from 'react';
import { Skeleton } from '@/components/ui/Skeleton';
import { useProjectStore } from '@/store/projectStore';
import { clamp, formatTimecode } from '@/utils/format';

const SCRUB_STEP = 0.01;

/**
 * The filmstrip timeline — Loopsmith's signature element. Thumbnails run
 * between sprocket-hole bands; the trim in/out points are film-tab range
 * thumbs (native inputs, so they're fully keyboard accessible); the amber
 * needle is the playhead, driven by rAF off the video element.
 */
export function Timeline({ videoRef }: { videoRef: RefObject<HTMLVideoElement | null> }) {
  const meta = useProjectStore((state) => state.meta);
  const trim = useProjectStore((state) => state.trim);
  const setTrim = useProjectStore((state) => state.setTrim);
  const filmstrip = useProjectStore((state) => state.filmstrip);

  const [currentTime, setCurrentTime] = useState(0);

  useEffect(() => {
    let frame = 0;
    const tick = () => {
      const video = videoRef.current;
      if (video) {
        const t = video.currentTime;
        setCurrentTime((prev) => (Math.abs(prev - t) > 0.02 ? t : prev));
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [videoRef]);

  if (!meta) return null;
  const duration = meta.duration;

  const seek = (time: number) => {
    const video = videoRef.current;
    const clamped = clamp(time, 0, duration);
    if (video) video.currentTime = clamped;
    setCurrentTime(clamped);
  };

  const startPct = (trim.start / duration) * 100;
  const endPct = (trim.end / duration) * 100;
  const playheadPct = (clamp(currentTime, 0, duration) / duration) * 100;

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[11px] text-ink-500 tabular-nums dark:text-ink-400">
        <span>
          <span className="text-ink-400 dark:text-ink-500">in </span>
          {formatTimecode(trim.start)}
        </span>
        <span>
          <span className="text-ink-400 dark:text-ink-500">out </span>
          {formatTimecode(trim.end)}
        </span>
        <span className="text-signal-600 dark:text-signal-400">
          <span className="text-ink-400 dark:text-ink-500">sel </span>
          {formatTimecode(trim.end - trim.start)}
        </span>
      </div>

      <div className="filmstrip relative h-16 select-none overflow-hidden rounded-lg bg-ink-950 ring-1 ring-ink-300 dark:ring-ink-700">
        {/* Thumbnails */}
        <div aria-hidden className="absolute inset-x-0 top-[7px] bottom-[7px] flex">
          {filmstrip.length > 0 ? (
            filmstrip.map((src, index) => (
              <img
                key={src}
                src={src}
                alt=""
                draggable={false}
                className="h-full min-w-0 flex-1 object-cover"
                style={{ opacity: 0.9 }}
                loading={index > 4 ? 'lazy' : undefined}
              />
            ))
          ) : (
            <Skeleton className="h-full w-full rounded-none bg-ink-800" />
          )}
        </div>

        {/* Dimmed regions outside the selection */}
        <div
          aria-hidden
          className="absolute inset-y-0 left-0 z-[3] bg-ink-950/70"
          style={{ width: `${startPct}%` }}
        />
        <div
          aria-hidden
          className="absolute inset-y-0 right-0 z-[3] bg-ink-950/70"
          style={{ width: `${100 - endPct}%` }}
        />

        {/* Selection frame */}
        <div
          aria-hidden
          className="absolute inset-y-0 z-[3] border-y-2 border-signal-500/90"
          style={{ left: `${startPct}%`, width: `${Math.max(0, endPct - startPct)}%` }}
        />

        {/* Playhead needle */}
        <div
          aria-hidden
          className="absolute inset-y-0 z-[4] w-0.5 bg-signal-500"
          style={{ left: `calc(${playheadPct}% - 1px)` }}
        >
          <div className="absolute -top-px left-1/2 size-2 -translate-x-1/2 rotate-45 rounded-[1px] bg-signal-500" />
        </div>

        {/* Scrub layer (invisible, full surface, keyboard seekable) */}
        <input
          type="range"
          aria-label="Playhead position"
          className="scrub-range z-[5]"
          min={0}
          max={duration}
          step={SCRUB_STEP}
          value={clamp(currentTime, 0, duration)}
          onChange={(event) => seek(Number(event.target.value))}
        />

        {/* Trim handles — film tabs */}
        <input
          type="range"
          aria-label="Trim start"
          aria-valuetext={formatTimecode(trim.start)}
          className="trim-range z-[6]"
          min={0}
          max={duration}
          step={SCRUB_STEP}
          value={trim.start}
          onChange={(event) => {
            const value = Number(event.target.value);
            setTrim({ start: value });
            seek(value);
          }}
        />
        <input
          type="range"
          aria-label="Trim end"
          aria-valuetext={formatTimecode(trim.end)}
          className="trim-range z-[6]"
          min={0}
          max={duration}
          step={SCRUB_STEP}
          value={trim.end}
          onChange={(event) => {
            const value = Number(event.target.value);
            setTrim({ end: value });
            seek(value);
          }}
        />
      </div>

      <div className="flex justify-between font-mono text-[10px] text-ink-400 tabular-nums dark:text-ink-500">
        <span>0:00.0</span>
        <span>{formatTimecode(duration / 2)}</span>
        <span>{formatTimecode(duration)}</span>
      </div>
    </div>
  );
}
